import React, { useState, useMemo, useEffect } from 'react';
import {
  Input,
  Button,
  Tooltip,
  Typography,
  Select,
  Switch,
  Space,
  Popconfirm,
  message,
  Spin,
  Tag,
} from 'antd';
import { CheckOutlined, DeleteOutlined, PlusOutlined, RollbackOutlined } from '@ant-design/icons';
import TableWithSearch from '../../components/TableWithSearch';
import DefaultAttributes from '../DefaultAttributes';
import { usePostClickTracking } from '@src/api/emp/queryHooks';

const { Title } = Typography;
const { Option } = Select;

const getSelectorDescription = (selector: string): string => {
  // Special case: [attr]:not([attr="value"])
  const notPattern = /^\[([^\]]+)\]:not\(\[([^\]=]+)="?([^\]"]+)"?\]\)$/;
  const notMatch = selector.match(notPattern);
  if (notMatch) {
    const [, attr, notAttr, notValue] = notMatch;
    if (attr === notAttr) {
      return `Matches all elements that have a '${attr}' attribute but are NOT set to '${notValue}'`;
    }
    return `Matches all elements with '${attr}', excluding elements with '${notAttr}="${notValue}"'`;
  }

  // If it doesn't start and end with [], treat as Tag
  if (!selector.startsWith('[') || !selector.endsWith(']')) {
    return `Matches <${selector}> elements`;
  }

  const attrMatch = selector.match(/^\[([^\^\$\*\=\]]+)(\^=|\$=|\*=|=)?"?([^\]]*?)"?\]$/);
  if (!attrMatch) return 'Unrecognized selector';

  const [, attr, operator, value] = attrMatch;

  if (!operator) {
    if (attr.startsWith('@') || attr.startsWith('v-on:') || attr.startsWith('v-on\\:')) {
      return `Vue directive: matches elements with the ${attr} directive`;
    }
    return `Matches elements with a '${attr}' attribute`;
  }

  switch (operator) {
    case '^=':
      return `Matches elements with a '${attr}' attribute starting with '${value}'`;
    case '$=':
      return `Matches elements with a '${attr}' attribute ending with '${value}'`;
    case '*=':
      return `Matches elements with a '${attr}' attribute containing '${value}'`;
    case '=':
      return `Matches elements with a '${attr}' attribute set to '${value}'`;
    default:
      return 'Unknown selector pattern';
  }
};

interface TableRowDefault {
  key: string;
  name: string;
  isEnabled: boolean;
  description: string;
}

const CssSelectors = ({
  cssSelectorsData,
  refetchClickTracking,
  setUnsavedChangesForCssSelectors,
  isLoadingClickTrackingData,
}: {
  cssSelectorsData: any;
  refetchClickTracking: any;
  setUnsavedChangesForCssSelectors: React.Dispatch<React.SetStateAction<number>>;
  isLoadingClickTrackingData: boolean;
}) => {
  const [deletedCustomRows, setDeletedCustomRows] = useState<Set<string>>(new Set());
  const [formList, setFormList] = useState<any[]>([]);
  const [tableDataDefault, setTableDataDefault] = useState<TableRowDefault[]>([]);

  const { mutate: createClickTracking, isLoading: isLoadingCreateClickTracking } =
    usePostClickTracking();

  const handleInputChange = (index, field, value) => {
    const updatedList = [...formList];
    updatedList[index][field] = value;
    setFormList(updatedList);
  };

  const handleAddCustomSelector = () => {
    setFormList([...formList, { type: 'Tag', name: '', condition: '', value: '' }]);
  };

  useEffect(() => {
    const defaultSelectors = cssSelectorsData?.value?.default;

    if (!defaultSelectors || typeof defaultSelectors !== 'object') {
      setTableDataDefault([]);
      return;
    }

    const mapped = Object.entries(defaultSelectors).map(([attr, isEnabled]) => ({
      key: attr,
      name: attr,
      displayName: attr.replace(/\\/g, '\\\\'),
      description: getSelectorDescription(attr),
      isEnabled: !!isEnabled,
      oldEnabledState: !!isEnabled,
    }));

    setTableDataDefault(mapped);
  }, [cssSelectorsData]);

  const isLoading = useMemo(
    () => isLoadingCreateClickTracking || isLoadingClickTrackingData,
    [isLoadingCreateClickTracking, isLoadingClickTrackingData],
  );

  const handleRemoveRow = (index) => {
    const updatedList = [...formList];
    updatedList.splice(index, 1);
    setFormList(updatedList);
  };

  const tableDataCustom = useMemo(() => {
    const customSelectors = cssSelectorsData?.value?.custom;

    if (!Array.isArray(customSelectors)) return [];

    return customSelectors.map((attr) => ({
      key: attr,
      name: attr,
      description: getSelectorDescription(attr),
      isDeleted: deletedCustomRows.has(attr),
    }));
  }, [cssSelectorsData, deletedCustomRows]);

  const customColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: '30%',
      render: (text, record) => (
        <span style={{ opacity: record.isDeleted ? 0.5 : 1 }} data-test-id="customSelectorName">
          {text}
        </span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: '60%',
      render: (text, record) => (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span
            style={{ opacity: record.isDeleted ? 0.5 : 1 }}
            data-test-id="customSelectorDescription"
          >
            {text}
          </span>
          {record.isDeleted ? (
            <div>
              <Tag color="orange">Deleted - save to apply, restore to revert</Tag>
              <Tooltip title="Restore">
                <Button
                  onClick={() => {
                    setDeletedCustomRows((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(record.name);

                      // Evaluate dirty state using the updated set

                      return newSet;
                    });
                  }}
                  type="text"
                  icon={<RollbackOutlined color="rgba(128, 163, 255, 1)" />}
                  data-test-id={`${record.name}-restoreAttributeButton`}
                />
              </Tooltip>
            </div>
          ) : (
            <Tooltip title="Delete">
              <Button
                onClick={() => {
                  setDeletedCustomRows((prev) => new Set(prev).add(record.name));
                }}
                type="text"
                icon={<DeleteOutlined />}
                danger
                data-test-id={`${record.name}-deleteAttributeButton`}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleToggleDefaultSelector = (name: string, isEnabled: boolean) => {
    setTableDataDefault((prev) =>
      prev.map((row) => (row.name === name ? { ...row, isEnabled } : row)),
    );
  };

  const defaultColumns = [
    {
      title: 'Name',
      dataIndex: 'displayName',
      width: '30%',
      render: (text, record) => <span data-test-id="defaultSelectorName">{text}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: '60%',
      render: (text, record) => (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>{text}</span>

          <span>
            {record.isEnabled !== record.oldEnabledState && (
              <Tag color="orange">{record.isEnabled ? 'Enabled' : 'Disabled'}, save to apply</Tag>
            )}
            <Switch
              size="small"
              data-test-id={`${record.name}-defaultSelectorToggle`}
              checked={record.isEnabled}
              onChange={(checked, e) => {
                handleToggleDefaultSelector(record.name, checked);
              }}
            />
          </span>
        </Space>
      ),
    },
  ];

  const handleSave = () => {
    // --- Validation: required + uniqueness ---
    const cleanedFormList = formList.map((it) => ({
      ...it,
      name: (it.name ?? '').trim(),
      value: (it.value ?? '').trim(),
      condition: (it.condition ?? '').trim(),
    }));

    // 1) Required fields
    for (const it of cleanedFormList) {
      if (!it.name) {
        message.error('Please enter a name for each newly added CSS selector row.');
        return;
      }
      if (it.type === 'Attribute' && it.condition && !it.value) {
        message.error('Please fill in all the mandatory fields in the form.');
        return;
      }
    }

    // 2) Uniqueness among form rows
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    cleanedFormList.forEach((it) => {
      const key = it.name.toLowerCase();
      if (seen.has(key)) duplicates.add(it.name);
      seen.add(key);
    });
    if (duplicates.size > 0) {
      message.error(
        `Each selector name in the form must be unique. Duplicates: ${[...duplicates].join(', ')}`,
      );
      return;
    }

    // 3) Check against existing custom selectors
    const existingCustom = cssSelectorsData?.value?.custom || [];
    const existingCustomNames = new Set(
      existingCustom.map((selector: string) => selector.toLowerCase()),
    );

    // 4) Check against existing default selectors
    const existingDefaultNames = new Set(tableDataDefault.map((row) => row.name.toLowerCase()));

    // Build new selectors from form and check for conflicts
    const conflictsWithExisting = new Set<string>();
    cleanedFormList.forEach((item) => {
      let newSelector = '';

      if (item.type === 'Tag' && item.name) {
        newSelector = item.name;
      } else if (item.type === 'Attribute' && item.name) {
        switch (item.condition) {
          case 'starts with':
            newSelector = `[${item.name}^="${item.value}"]`;
            break;
          case 'ends with':
            newSelector = `[${item.name}$="${item.value}"]`;
            break;
          case 'equals':
            newSelector = `[${item.name}="${item.value}"]`;
            break;
          case 'contains':
            newSelector = `[${item.name}*="${item.value}"]`;
            break;
          default:
            newSelector = `[${item.name}]`;
        }
      }

      const lowerSelector = newSelector.toLowerCase();
      if (existingCustomNames.has(lowerSelector) || existingDefaultNames.has(lowerSelector)) {
        conflictsWithExisting.add(newSelector);
      }
    });

    if (conflictsWithExisting.size > 0) {
      message.error(
        `The following selectors already exist: ${[...conflictsWithExisting].join(', ')}`,
      );
      return;
    }

    const selectors: string[] = [];

    // 1. Include non-deleted custom selectors from table
    existingCustom.forEach((item: string) => {
      if (!deletedCustomRows.has(item)) {
        selectors.push(item);
      }
    });

    // 2. Include form-created selectors (use cleaned list)
    cleanedFormList.forEach((item) => {
      if (item.type === 'Tag' && item.name) {
        selectors.push(`${item.name}`);
      } else if (item.type === 'Attribute' && item.name) {
        switch (item.condition) {
          case 'starts with':
            selectors.push(`[${item.name}^="${item.value}"]`);
            break;
          case 'ends with':
            selectors.push(`[${item.name}$="${item.value}"]`);
            break;
          case 'equals':
            selectors.push(`[${item.name}="${item.value}"]`);
            break;
          case 'contains':
            selectors.push(`[${item.name}*="${item.value}"]`);
            break;
          default:
            // No condition picked → attribute presence selector
            selectors.push(`[${item.name}]`);
        }
      }
    });

    // 3. Include enabled default selectors
    const enabledDefaults = tableDataDefault.filter((row) => row.isEnabled).map((row) => row.name);

    // Final object
    const finalOutput = {
      en: true,
      cssSelectors: [...selectors, ...enabledDefaults],
    };

    createClickTracking(
      { queryBody: finalOutput },
      {
        onSuccess: () => {
          message.success({
            content: (
              <div style={{ textAlign: 'left' }}>
                Changes to the configuration have been saved successfully.
                <br />
                Sensors may take up to 5 minutes to reflect changes.
              </div>
            ),
            duration: 5,
          });

          setFormList([]);
          setUnsavedChangesForCssSelectors(0);
          setDeletedCustomRows(new Set());
          refetchClickTracking();
        },
        onError: (error) => {
          const errorMsg =
            error?.response?.data?.msg ||
            error?.msg ||
            error?.message ||
            'An unexpected error occurred.';
          message.error(errorMsg);
        },
      },
    );
  };

  const unsavedCount =
    formList.length +
    deletedCustomRows.size +
    tableDataDefault.filter(
      (row) => row.isEnabled !== (cssSelectorsData?.value?.default?.[row.name] ?? false),
    ).length;

  useEffect(() => {
    setUnsavedChangesForCssSelectors(unsavedCount);
  }, [unsavedCount, setUnsavedChangesForCssSelectors]);

  return isLoading ? (
    <Spin
      spinning={isLoading}
      style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
    />
  ) : (
    <div style={{ paddingRight: 16, position: 'relative' }}>
      <DefaultAttributes
        attributes={[
          'button',
          'a',

          'select',
          'textarea',
          'input:not([type="hidden"])',
          'summary',
          'video',
          'audio',
        ]}
        name="Default Native Clickable Selectors"
      />
      <div
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          backgroundColor: '#fff',
          overflow: 'visible',
        }}
      >
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #f0f0f0',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Custom CSS Selectors
        </div>
        <div style={{ padding: 0, overflow: 'auto', maxWidth: '100%' }}>
          <TableWithSearch
            columns={customColumns}
            data={tableDataCustom}
            pagination={false}
            searchKeys={['name', 'description']}
            headerOffset={500}
          />
        </div>
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #f0f0f0',
            maxHeight: formList.length > 10 ? 400 : 'auto',
            overflowY: formList.length > 10 ? 'auto' : 'visible',
          }}
        >
          {formList.map((form, index) => {
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: 8,
                  marginTop: 8,
                  marginBottom: 8,
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                }}
              >
                <Select
                  placeholder="Type *"
                  value={form.type}
                  onChange={(val) => handleInputChange(index, 'type', val)}
                  style={{ width: 120 }}
                  data-test-id="customCSSSelectorTypeSelect"
                >
                  <Option value="Tag">Tag</Option>
                  <Option value="Attribute">Attribute</Option>
                </Select>

                {form.type && (
                  <Input
                    placeholder="Name *"
                    value={form.name}
                    onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                    style={{ width: 180 }}
                    data-test-id="customCSSSelectorNameInput"
                  />
                )}

                {form.type === 'Attribute' && (
                  <Select
                    placeholder="Condition"
                    value={form.condition || undefined}
                    onChange={(val) => handleInputChange(index, 'condition', val)}
                    style={{ width: 140 }}
                    data-test-id="attributeConditionSelect"
                  >
                    <Option value="starts with">Starts with</Option>
                    <Option value="ends with">Ends with</Option>
                    <Option value="equals">Equals</Option>
                    <Option value="contains">Contains</Option>
                  </Select>
                )}

                {form.type === 'Attribute' && (
                  <Input
                    placeholder="Value"
                    value={form.value}
                    onChange={(e) => handleInputChange(index, 'value', e.target.value)}
                    style={{ width: 180 }}
                    data-test-id="attributeValueInput"
                  />
                )}

                <Button
                  icon={<DeleteOutlined />}
                  type="text"
                  danger
                  onClick={() => handleRemoveRow(index)}
                  data-test-id="deleteCustomSelectorButton"
                />
              </div>
            );
          })}

          <Button
            icon={<PlusOutlined />}
            onClick={handleAddCustomSelector}
            type="default"
            data-test-id="addCssSelectorButton"
          >
            Add CSS Selectors
          </Button>
        </div>
      </div>

      <div
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          marginTop: 16,
          backgroundColor: '#fff',
          overflow: 'visible',
        }}
      >
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #f0f0f0',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Default Virtual Clickable Selectors
        </div>
        <div style={{ padding: 0, marginBottom: '40px', overflow: 'auto', maxWidth: '100%' }}>
          <TableWithSearch
            columns={defaultColumns}
            data={tableDataDefault}
            pagination={false}
            searchKeys={['name', 'description']}
            headerOffset={650}
          />
        </div>
      </div>
      {cssSelectorsData?.metadata?.updated_at ? (
        <div
          style={{
            marginTop: '24px',
            color: '#8c8c8c',
            fontSize: 14,
            fontWeight: 400,
          }}
          data-test-id="cssSelectorsLastUpdated"
        >
          Last Updated on{' '}
          {new Date(cssSelectorsData?.metadata?.updated_at * 1000).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}{' '}
          by {cssSelectorsData?.metadata?.updated_by}
        </div>
      ) : null}
      {unsavedCount > 0 ? (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 'calc(24px + env(safe-area-inset-bottom))',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            background: '#fff',
            padding: '14px 50px',
            borderRadius: 16,
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            minWidth: 420,
            maxWidth: '90vw',
          }}
          aria-live="polite"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
            }}
          >
            <span
              style={{
                color: 'rgba(31, 31, 31, 1)',
                fontWeight: 400,
                fontSize: 14,
              }}
              data-test-id="unsavedChangesWarning"
            >
              You have {unsavedCount} unsaved change{unsavedCount !== 1 ? 's' : ''}
            </span>

            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleSave}
              data-test-id="saveCssSelectorsButton"
              loading={isLoadingCreateClickTracking}
              disabled={isLoadingCreateClickTracking}
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CssSelectors;
